import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "@/components/ErrorBoundary";

// Eagerly loaded: the two primary landing/conversion pages, kept in the entry
// bundle so their LCP is not delayed by an extra chunk fetch.
import Index from "./pages/Index";
import GetThePillOnline from "./pages/GetThePillOnline";
import CookieConsent from "./components/consent/CookieConsent";

// Lazily loaded: every other route is split into its own chunk (loaded only when
// visited). This keeps the entry bundle small for better mobile INP/LCP.
const NotFound = lazy(() => import("./pages/NotFound"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const ReminderPage = lazy(() => import("./pages/ReminderPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage"));
const IsThePillFree = lazy(() => import("./pages/guides/IsThePillFree"));
const MicrogynonVsRigevidon = lazy(() => import("./pages/guides/MicrogynonVsRigevidon"));
const YasminVsLucette = lazy(() => import("./pages/guides/YasminVsLucette"));
const MorningAfterVsRegularPill = lazy(() => import("./pages/guides/MorningAfterVsRegularPill"));
const AreYouOverpayingForYourPill = lazy(() => import("./pages/guides/AreYouOverpayingForYourPill"));
const HowEffectiveIsThePill = lazy(() => import("./pages/guides/HowEffectiveIsThePill"));
const AlcoholAndThePill = lazy(() => import("./pages/guides/AlcoholAndThePill"));
const HowOldToGoOnThePill = lazy(() => import("./pages/guides/HowOldToGoOnThePill"));
const NhsPharmacyContraceptionService = lazy(() => import("./pages/guides/NhsPharmacyContraceptionService"));
const ContraceptivePillNames = lazy(() => import("./pages/guides/ContraceptivePillNames"));
const BleedingOnThePill = lazy(() => import("./pages/guides/BleedingOnThePill"));
const IsContraceptionDirectSafe = lazy(() => import("./pages/guides/IsContraceptionDirectSafe"));
const SideEffectsOfThePill = lazy(() => import("./pages/guides/SideEffectsOfThePill"));
const WhatIsTheMiniPill = lazy(() => import("./pages/guides/WhatIsTheMiniPill"));
const WhatIsTheCombinedPill = lazy(() => import("./pages/guides/WhatIsTheCombinedPill"));
const CombinedPillVsMiniPill = lazy(() => import("./pages/guides/CombinedPillVsMiniPill"));
const HowToGetThePill = lazy(() => import("./pages/guides/HowToGetThePill"));
const MiniPillWeightGain = lazy(() => import("./pages/guides/MiniPillWeightGain"));
const DoesThePillStopYourPeriod = lazy(() => import("./pages/guides/DoesThePillStopYourPeriod"));
const OrderMiniPill = lazy(() => import("./pages/OrderMiniPill"));
const OrderCombinedPill = lazy(() => import("./pages/OrderCombinedPill"));
const RepeatPillPrescription = lazy(() => import("./pages/RepeatPillPrescription"));
const WhichPillRightForMe = lazy(() => import("./pages/guides/WhichPillRightForMe"));
const CerelleVsCerazette = lazy(() => import("./pages/guides/CerelleVsCerazette"));
const MissedPillWhatToDo = lazy(() => import("./pages/guides/MissedPillWhatToDo"));
const StartingThePill = lazy(() => import("./pages/guides/StartingThePill"));
const SwitchingPill = lazy(() => import("./pages/guides/SwitchingPill"));
const MicrogynonGuide = lazy(() => import("./pages/guides/MicrogynonGuide"));
const CerelleGuide = lazy(() => import("./pages/guides/CerelleGuide"));
const SlyndGuide = lazy(() => import("./pages/guides/SlyndGuide"));
const PillAndBloodClots = lazy(() => import("./pages/guides/PillAndBloodClots"));
const PillAndAcne = lazy(() => import("./pages/guides/PillAndAcne"));
const FreeContraceptionOnline = lazy(() => import("./pages/guides/FreeContraceptionOnline"));
const YasminGuide = lazy(() => import("./pages/guides/YasminGuide"));
const LevestGuide = lazy(() => import("./pages/guides/LevestGuide"));
const GedarelGuide = lazy(() => import("./pages/guides/GedarelGuide"));
const MarvelonGuide = lazy(() => import("./pages/guides/MarvelonGuide"));
const NoridayGuide = lazy(() => import("./pages/guides/NoridayGuide"));
const RigevidonGuide = lazy(() => import("./pages/guides/RigevidonGuide"));
const DesogestrelGuide = lazy(() => import("./pages/guides/DesogestrelGuide"));
const CerazetteGuide = lazy(() => import("./pages/guides/CerazetteGuide"));
const BestContraceptivePill = lazy(() => import("./pages/guides/BestContraceptivePill"));
const PillAndAntibiotics = lazy(() => import("./pages/guides/PillAndAntibiotics"));
const ComingOffThePill = lazy(() => import("./pages/guides/ComingOffThePill"));
const PillAndMood = lazy(() => import("./pages/guides/PillAndMood"));
const SickOnThePill = lazy(() => import("./pages/guides/SickOnThePill"));
const HowToDelayYourPeriod = lazy(() => import("./pages/guides/HowToDelayYourPeriod"));
const PeriodDelayTablets = lazy(() => import("./pages/PeriodDelayTablets"));
const FAQsPage = lazy(() => import("./pages/FAQsPage"));
const AboutUsPage = lazy(() => import("./pages/AboutUsPage"));
const AuthorUsamaAli = lazy(() => import("./pages/AuthorUsamaAli"));
const GuidesPage = lazy(() => import("./pages/GuidesPage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const ContactUsPage = lazy(() => import("./pages/ContactUsPage"));
const MissedPillToolPage = lazy(() => import("./pages/MissedPillToolPage"));
const StyleguidePage = lazy(() => import("./pages/StyleguidePage"));

const queryClient = new QueryClient();

// Snapshot of the prerendered fallback markup, captured before React clears
// #root. This module's body is evaluated during main.tsx's import, i.e. before
// createRoot().render(), so the prerendered DOM is still present here. The
// markup is our own build-time output from static-prerender.mjs, never user
// input, so setting it as HTML is safe.
const PRERENDERED_HTML =
  typeof document !== "undefined"
    ? document.getElementById("seo-fallback")?.innerHTML ?? ""
    : "";
const INITIAL_PATH = typeof window !== "undefined" ? window.location.pathname : "";

// Fallback shown while a route chunk loads.
//
// On the initial load of a lazy route, React's first commit removes the
// prerendered DOM, so rendering a blank placeholder here left the page with no
// content for as long as the route chunk took to arrive (measured ~389ms
// locally, scaling 1:1 with chunk latency). Re-rendering the prerendered markup
// instead keeps the same content on screen continuously until the real page is
// ready. The eager routes (/, /get-the-pill-online) never suspend and are
// unaffected either way.
//
// The snapshot is only correct for the URL that was prerendered: after a
// client-side navigation it would show the wrong page, so anything past the
// first route falls back to the unobtrusive placeholder.
//
// LAYOUT: the snapshot is absolutely positioned inside a `min-h-screen` box, so
// this fallback occupies exactly the same space as the plain placeholder below
// no matter how tall the prerendered markup is. Letting the snapshot size the
// container instead pushed the persistent siblings after </Suspense> (the
// eagerly mounted CookieConsent and toasters) down the page, and moved them back
// when the real route swapped in — a reproducible 0.108 CLS on guide pages.
// Keeping the document height constant across the swap keeps CLS at 0.
const RouteFallback = () => {
  if (PRERENDERED_HTML && window.location.pathname === INITIAL_PATH) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-background" aria-hidden="true">
        <div
          id="seo-fallback"
          className="absolute inset-x-0 top-0"
          dangerouslySetInnerHTML={{ __html: PRERENDERED_HTML }}
        />
      </div>
    );
  }
  return <div className="min-h-screen bg-background" aria-hidden="true" />;
};

const App = () => {
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter basename="/">
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/welcome" element={<Index />} />
                <Route path="/get-the-pill-online" element={<GetThePillOnline />} />
                <Route path="/get-the-pill-online/mini-pill" element={<OrderMiniPill />} />
                <Route path="/get-the-pill-online/combined-pill" element={<OrderCombinedPill />} />
                <Route path="/get-the-pill-online/repeat-prescription" element={<RepeatPillPrescription />} />
                <Route path="/reminder" element={<ReminderPage />} />
                <Route path="/book" element={<BookingPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy-policy" element={<PrivacyPage />} />
                <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                <Route path="/accessibility" element={<AccessibilityPage />} />
                <Route path="/guides/is-the-contraceptive-pill-free" element={<IsThePillFree />} />
                <Route path="/guides/is-contraception-direct-safe" element={<IsContraceptionDirectSafe />} />
                <Route path="/guides/side-effects-of-the-pill" element={<SideEffectsOfThePill />} />
                <Route path="/guides/what-is-the-mini-pill" element={<WhatIsTheMiniPill />} />
                <Route path="/guides/what-is-the-combined-pill" element={<WhatIsTheCombinedPill />} />
                <Route path="/guides/combined-pill-vs-mini-pill" element={<CombinedPillVsMiniPill />} />
                <Route path="/guides/how-to-get-the-pill" element={<HowToGetThePill />} />
                <Route path="/guides/does-the-mini-pill-cause-weight-gain" element={<MiniPillWeightGain />} />
                <Route path="/guides/does-the-pill-stop-your-period" element={<DoesThePillStopYourPeriod />} />
                <Route path="/guides/which-contraceptive-pill-is-right-for-me" element={<WhichPillRightForMe />} />
                <Route path="/guides/cerelle-vs-cerazette" element={<CerelleVsCerazette />} />
                <Route path="/guides/microgynon-vs-rigevidon" element={<MicrogynonVsRigevidon />} />
                <Route path="/guides/yasmin-vs-lucette" element={<YasminVsLucette />} />
                <Route path="/guides/morning-after-pill-vs-regular-pill" element={<MorningAfterVsRegularPill />} />
                <Route path="/guides/are-you-overpaying-for-your-pill" element={<AreYouOverpayingForYourPill />} />
                <Route path="/guides/how-effective-is-the-contraceptive-pill" element={<HowEffectiveIsThePill />} />
                <Route path="/guides/alcohol-and-the-pill" element={<AlcoholAndThePill />} />
                <Route path="/guides/how-old-do-you-have-to-be-to-go-on-the-pill" element={<HowOldToGoOnThePill />} />
                <Route path="/guides/nhs-pharmacy-contraception-service" element={<NhsPharmacyContraceptionService />} />
                <Route path="/guides/contraceptive-pill-names" element={<ContraceptivePillNames />} />
                <Route path="/guides/bleeding-on-the-pill" element={<BleedingOnThePill />} />
                <Route path="/guides/missed-pill-what-to-do" element={<MissedPillWhatToDo />} />
                <Route path="/guides/starting-the-pill" element={<StartingThePill />} />
                <Route path="/guides/switching-contraceptive-pill" element={<SwitchingPill />} />
                <Route path="/guides/microgynon" element={<MicrogynonGuide />} />
                <Route path="/guides/cerelle" element={<CerelleGuide />} />
                <Route path="/guides/slynd" element={<SlyndGuide />} />
                <Route path="/guides/pill-and-blood-clots" element={<PillAndBloodClots />} />
                <Route path="/guides/pill-and-acne" element={<PillAndAcne />} />
                <Route path="/guides/free-contraception-online" element={<FreeContraceptionOnline />} />
                <Route path="/guides/yasmin" element={<YasminGuide />} />
                <Route path="/guides/levest" element={<LevestGuide />} />
                <Route path="/guides/gedarel" element={<GedarelGuide />} />
                <Route path="/guides/marvelon" element={<MarvelonGuide />} />
                <Route path="/guides/noriday" element={<NoridayGuide />} />
                <Route path="/guides/rigevidon" element={<RigevidonGuide />} />
                <Route path="/guides/desogestrel" element={<DesogestrelGuide />} />
                <Route path="/guides/cerazette" element={<CerazetteGuide />} />
                <Route path="/guides/best-contraceptive-pill" element={<BestContraceptivePill />} />
                <Route path="/guides/pill-and-antibiotics" element={<PillAndAntibiotics />} />
                <Route path="/guides/coming-off-the-pill" element={<ComingOffThePill />} />
                <Route path="/guides/pill-and-mood" element={<PillAndMood />} />
                <Route path="/guides/vomiting-diarrhoea-and-the-pill" element={<SickOnThePill />} />
                <Route path="/guides/how-to-delay-your-period" element={<HowToDelayYourPeriod />} />
                <Route path="/period-delay-tablets" element={<PeriodDelayTablets />} />
                <Route path="/faqs" element={<FAQsPage />} />
                <Route path="/about-us" element={<AboutUsPage />} />
                <Route path="/authors/usama-ali" element={<AuthorUsamaAli />} />
                <Route path="/guides" element={<GuidesPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/contact-us" element={<ContactUsPage />} />
                <Route path="/missed-pill" element={<MissedPillToolPage />} />
                <Route path="/styleguide" element={<StyleguidePage />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              <CookieConsent />
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
